<template>
    <div v-show="keywords && keywords.length" class="SuggestedKeywords flex flex-col items-start gap-2">
        <div class="flex flex-col gap-2 md:flex-row">
            <div v-if="label" class="gray-600 mb-1.5 self-start text-sm md:mt-2.5 md:whitespace-nowrap">
                {{ label }}
            </div>

            <div v-if="variant === 'hashtags'" class="flex flex-wrap items-center gap-x-4 gap-y-2">
                <button
                    v-for="keyword in keywordsToRender"
                    :key="keyword"
                    type="button"
                    class="text-sm font-semibold leading-5 text-[#0f4f4a] transition-colors hover:text-[#0a3e3a]"
                    @click="$emit('select-keyword', keyword)"
                >
                    #{{ keyword }}
                </button>
            </div>
            <CTags
                v-else
                :tags="keywords"
                :max-tags="maxTags"
                variant="gray"
                @tag-click="$emit('select-keyword', $event)"
            />
        </div>
        <CAlert v-if="isGivingTuesdayActive && showInfoMessage" class="w-full" icon="info" variant="info"
            >Pridajte si kľúčové slovo #GivingTuesday -> zaradíme vás do kampane, ktorú pripravujeme spolu s Pontisom k
            3. 12. Nič vás to nestojí. V ten deň sa po celom svete oslavujú dobré skutky.
            <a href="https://growni.sk/blog/giving-tuesday" target="_blank"> Viac o Giving Tuesday.</a>
        </CAlert>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { staticKeywordsDefault } from '@/constants/keywords/default'

const route = useRoute()

const props = defineProps({
    label: {
        type: String,
        default: ''
    },
    maxSuggestions: {
        type: Number,
        default: undefined
    },
    additionalKeywords: {
        type: Array as () => string[],
        default: () => []
    },
    selectedKeywords: {
        type: Array as () => string[],
        default: () => []
    },
    suggestedKeywords: {
        type: Array as () => string[],
        default: () => [...staticKeywordsDefault]
    },
    maxTags: {
        type: Number,
        default: 5
    },
    variant: {
        type: String as () => 'default' | 'hashtags',
        default: 'default'
    }
})

defineEmits(['select-keyword'])

const keywords = computed((): string[] => {
    const suggestedKeywordsToShow = props.suggestedKeywords.slice()

    if (isGivingTuesdayActive.value) {
        suggestedKeywordsToShow.push('GivingTuesday')
    }

    const maxSuggestedCount = props.maxSuggestions
        ? props.maxSuggestions + (props.additionalKeywords.length || 0)
        : undefined

    const selectedKeywordsArray = Array.isArray(props.selectedKeywords) ? props.selectedKeywords : []
    const filteredKeywords = [...props.additionalKeywords, ...suggestedKeywordsToShow]
        .slice(0, maxSuggestedCount)
        .filter((keyword) => !selectedKeywordsArray.includes(keyword))

    return filteredKeywords
})

const keywordsToRender = computed((): string[] => {
    if (props.maxTags > 0) {
        return keywords.value.slice(0, props.maxTags)
    }
    return keywords.value
})

const showInfoMessage = computed((): boolean => {
    return route.name === 'profile-projects-create'
})

const isGivingTuesdayActive = computed((): boolean => {
    const currentDateTimestamp = new Date().getTime()
    const currentYear = new Date().getFullYear()
    const givingTuesdayStartDate = new Date(`${currentYear}-10-20`).getTime()

    let givingTuesdayEndDate
    if (route.name === 'profile-projects-create') {
        givingTuesdayEndDate = new Date(`${currentYear}-12-03`).getTime() // 3.12.2024
    } else if (route.name === 'dobrovolnictvo-dobrovolnicke-projekty') {
        // Tag is visible on projects page longer than in create page :)
        givingTuesdayEndDate = new Date(`${currentYear}-12-31`).getTime() // 31.12.2024
    } else {
        return false
    }

    return currentDateTimestamp >= givingTuesdayStartDate && currentDateTimestamp <= givingTuesdayEndDate
})
</script>
